import "./IronCondorIdeaPayoffComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {TradingIdeasViewStore} from "../../../../store/TradingIdeasViewStore";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {IronCondorValueDomain} from "./models/IronCondorValueDomain";
import {IronCondorTradeIdeaRecord, isIronCondorIdea} from "../../../../models/TradeIdeaRecord";
import {ChartValuePosition, ValuePosition} from "./models/ChartValuePosition";
import {ChartBox, ChartBoxPosition} from "./models/ChartBoxPosition";
import {
    TradingIdeasEquityPriceFormatStrategy,
    TradingIdeasIndexPriceFormatStrategy
} from "../common/TradingIdeasPriceFormatStrategy";
import {PriceStrategy} from "../../../../../components/inputs/price/input/PriceStrategy";
import {SecurityUtil} from "../../../../../js/util/SecurityUtil";

export const IronCondor = "tfIronCondorIdeaPayoff";

export function ironCondor(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/iron-condor/IronCondorIdeaPayoffComponent.html`,
        bindings: {
            store: "<"
        },
        controllerAs: "ctrl",
        controller: IronCondorIdeaPayoffComponentController
    };
}

export class IronCondorIdeaPayoffComponentController implements IOnInit, IOnDestroy {

    /** @Input */
    public readonly store: TradingIdeasViewStore;

    private readonly record$ = new ReplaySubject<IronCondorTradeIdeaRecord>(1);
    private readonly formatter$ = new BehaviorSubject<PriceStrategy>(TradingIdeasEquityPriceFormatStrategy.Instance);

    private readonly domain$ = new ReplaySubject<IronCondorValueDomain>(1);
    private readonly boughtPut$ = new ReplaySubject<number>(1);
    private readonly soldPut$ = new ReplaySubject<number>(1);
    private readonly lowerBreakEven$ = new ReplaySubject<number | null>(1);
    private readonly currentPrice$ = new ReplaySubject<number>(1);
    private readonly soldCall$ = new ReplaySubject<number>(1);
    private readonly upperBreakEven$ = new ReplaySubject<number | null>(1);
    private readonly boughtCall$ = new ReplaySubject<number>(1);

    private readonly lowerBreakEvenPosition$ = new ReplaySubject<ChartValuePosition>(1);
    private readonly soldPutPosition$ = new ReplaySubject<ChartValuePosition>(1);
    private readonly currentPricePosition$ = new ReplaySubject<ChartValuePosition>(1);
    private readonly soldCallPosition$ = new ReplaySubject<ChartValuePosition>(1);
    private readonly upperBreakEvenPosition$ = new ReplaySubject<ChartValuePosition>(1);

    private readonly lowerLossBoxPosition$ = new ReplaySubject<ChartBoxPosition>(1);
    private readonly lowerBreakEvenBoxPosition$ = new ReplaySubject<ChartBoxPosition>(1);
    private readonly profitBoxPosition$ = new ReplaySubject<ChartBoxPosition>(1);
    private readonly upperBreakEvenBoxPosition$ = new ReplaySubject<ChartBoxPosition>(1);
    private readonly upperLossBoxPosition$ = new ReplaySubject<ChartBoxPosition>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public $onInit(): void {
        this.watchModelChange();
        this.watchRecordChange();
        this.watchDomainChange();
        this.watchValueChanges();
        this.watchLowerBreakEvenPosition();
        this.watchSoldPutPosition();
        this.watchCurrentPricePosition();
        this.watchSoldCallPosition();
        this.watchUpperBreakEvenPosition();
        this.watchLowerLossBoxPosition();
        this.watchLowerBreakEvenBoxPosition();
        this.watchProfitBoxPosition();
        this.watchUpperBreakEvenBoxPosition();
        this.watchUpperLossBoxPosition();
    }

    private watchModelChange() {
        this.store
            .currentModel
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((model) => model.idea)
            .filter((idea) => isIronCondorIdea(idea))
            .map((idea) => idea as IronCondorTradeIdeaRecord)
            .distinctUntilChanged((a, b) => this.modelUnchanged(a, b))
            .subscribe((idea) => this.record$.next(idea));
    }

    private modelUnchanged(previous: IronCondorTradeIdeaRecord, next: IronCondorTradeIdeaRecord): boolean {
        return (previous.underlying === next.underlying) &&
            (previous.boughtPut.strike === next.boughtPut.strike) &&
            (previous.boughtCall.strike === next.boughtCall.strike);
    }

    private watchRecordChange() {
        this.modelChanges
            .map((record) => record.underlying)
            .map((underlying) => this.formatterForUnderlying(underlying))
            .subscribe((formatter) => this.formatter$.next(formatter));
    }

    private formatterForUnderlying(underlying: string): PriceStrategy {
        if (SecurityUtil.isIndex(underlying)) {
            return TradingIdeasIndexPriceFormatStrategy.Instance;
        } else {
            return TradingIdeasEquityPriceFormatStrategy.Instance;
        }
    }

    private watchDomainChange() {
        this.modelChanges
            .map((idea) => IronCondorValueDomain.create(idea))
            .subscribe((domain) => this.domain$.next(domain));
    }

    private get modelChanges(): Observable<IronCondorTradeIdeaRecord> {
        return this.record$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async);
    }

    private watchValueChanges() {
        this.modelChanges
            .subscribe((record) => {
                this.boughtPut$.next(record.boughtPut.strike);
                this.soldPut$.next(record.soldPut.strike);
                this.soldCall$.next(record.soldCall.strike);
                this.boughtCall$.next(record.boughtCall.strike);

                this.lowerBreakEven$.next(record.lowerBreakEven);
                this.upperBreakEven$.next(record.upperBreakEven);

                this.currentPrice$.next(record.currentPrice);
            });
    }

    /*
     * If for some reason we get a null lower break even then this returns
     * the position of the sold put line instead so we don't break the chart layout too much
     */
    private watchLowerBreakEvenPosition() {
        const source$ = Observable.combineLatest(this.lowerBreakEven$, this.domain$, this.soldPut$);
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([value, domain, soldPut]) => {
                   if (value == null) {
                       return domain.position(soldPut);
                   } else {
                       return domain.position(value);
                   }
               })
               .map(ValuePosition)
               .subscribe((position) => this.lowerBreakEvenPosition$.next(position));
    }

    private watchSoldPutPosition() {
        const source$ = Observable.combineLatest(this.soldPut$, this.domain$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([value, domain]) => domain.position(value))
               .map(ValuePosition)
               .subscribe((position) => this.soldPutPosition$.next(position));
    }

    private watchCurrentPricePosition() {
        const source$ = Observable.combineLatest(this.currentPrice$, this.domain$);
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([value, domain]) => domain.position(value))
               .map(ValuePosition)
               .subscribe((position) => this.currentPricePosition$.next(position));
    }

    private watchSoldCallPosition() {
        const source$ = Observable.combineLatest(this.soldCall$, this.domain$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([value, domain]) => domain.position(value))
               .map(ValuePosition)
               .subscribe((position) => this.soldCallPosition$.next(position));
    }

    private watchUpperBreakEvenPosition() {
        const source$ = Observable.combineLatest(this.upperBreakEven$, this.domain$, this.soldCall$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([value, domain, soldCall]) => {
                   if (value == null) {
                       return domain.position(soldCall);
                   } else {
                       return domain.position(value);
                   }
               })
               .map(ValuePosition)
               .subscribe((position) => this.upperBreakEvenPosition$.next(position));
    }

    private watchLowerLossBoxPosition() {
        this.lowerBreakEvenPosition$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((pos) => pos.left)
            .map((right) => ChartBox(0, right))
            .subscribe((position) => this.lowerLossBoxPosition$.next(position));
    }

    private watchLowerBreakEvenBoxPosition() {
        const source$ = Observable.combineLatest(this.lowerBreakEvenPosition$, this.soldPutPosition$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([lbe, sp]) => ChartBox(lbe.left, sp.left))
               .subscribe((position) => this.lowerBreakEvenBoxPosition$.next(position));
    }

    private watchProfitBoxPosition() {
        const source$ = Observable.combineLatest(this.soldPutPosition$, this.soldCallPosition$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([spp, scp]) => ChartBox(spp.left, scp.left))
               .subscribe((position) => this.profitBoxPosition$.next(position));
    }

    private watchUpperBreakEvenBoxPosition() {
        const source$ = Observable.combineLatest(this.soldCallPosition$, this.upperBreakEvenPosition$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([scp, ube]) => ChartBox(scp.left, ube.left))
               .subscribe((position) => this.upperBreakEvenBoxPosition$.next(position));
    }

    private watchUpperLossBoxPosition() {
        this.upperBreakEvenPosition$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((ube) => ube.left)
            .map((left) => ChartBox(left, 0))
            .subscribe((position) => this.upperLossBoxPosition$.next(position));
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
